import { memo, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NumberFlow from '@number-flow/react';
import FilterListIcon from '@mui/icons-material/FilterList';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { readableDropDareDifficultyLevels } from 'root/src/shared/constants/dropEvent';
import { providersLabelMap } from 'root/src/shared/constants/providers';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignCreateDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignCreateDareModal';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from 'root/src/client/v2/common/components/ui/popover';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { RedesignDareCard } from 'root/src/client/v2/_pages/drop/redesign/RedesignDareCard';
import { RedesignDareRow } from 'root/src/client/v2/_pages/drop/redesign/RedesignDareRow';
import { DropDaresLeftBadge } from 'root/src/client/v2/_pages/drop/redesign/DropDaresLeftBadge';
import { PaginatedTable } from 'root/src/client/v2/common/components/PaginatedTable';
import { useWindowSize } from 'root/src/client/v2/common/hooks/useWindowSize';
import { useLocalStorage } from 'root/src/client/web/hooks/useLocalStorage';
import { columnsForWidth } from 'root/src/client/v2/_pages/drop/redesign/dropGrid.helpers';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const DIFFICULTY_ORDER = ['n00b', 'easy', 'medium', 'hard', 'legendary'];
const DARES_ROWS_PER_PAGE = 3;
// Rows are much shorter than cards, so list view pages by a fixed row count instead of the
// columns-times-rows sizing the grid uses.
const LIST_ROWS_PER_PAGE = 9;
// Matches what the card actually puts on screen: the title and description, the reward's product name,
// and for a bundle the names of the items inside it (the card lists those in its tooltip). `query` is
// expected already trimmed and lower-cased by the caller.
const matchesQuery = (dare, query) => {
    var _a, _b, _c;
    return [
        dare.title,
        dare.description,
        (_a = dare.product) === null || _a === void 0 ? void 0 : _a.name,
        ...((_c = (_b = dare.product) === null || _b === void 0 ? void 0 : _b.rewards) !== null && _c !== void 0 ? _c : []).map(reward => reward.name),
    ].some(value => (value !== null && value !== void 0 ? value : '').toLowerCase().includes(query));
};
const toggleValue = (selected, value) => (selected.includes(value) ? selected.filter(item => item !== value) : [...selected, value]);
const groupLabelClass = 'mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60';
const DifficultyChips = ({ options, selected, onToggle }) => (<div className="flex flex-wrap gap-2">
		{options.map((option) => {
        var _a;
        const active = selected.includes(option);
        return (<button key={option} type="button" onClick={() => onToggle(option)} className={cn('rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors', active ? 'bg-primary text-white' : 'bg-white/[0.04] text-muted-foreground hover:bg-secondary hover:text-foreground')}>
					{(_a = readableDropDareDifficultyLevels[option]) !== null && _a !== void 0 ? _a : option}
				</button>);
    })}
	</div>);
const PlatformChips = ({ options, selected, onToggle }) => (<TooltipProvider delayDuration={200}>
		<div className="flex flex-wrap items-center gap-2">
			{options.map((option) => {
        var _a, _b;
        const active = selected.includes(option);
        return (<Tooltip key={option}>
						<TooltipTrigger asChild>
							<button type="button" onClick={() => onToggle(option)} aria-label={(_a = providersLabelMap[option]) !== null && _a !== void 0 ? _a : option} className={cn('flex items-center justify-center rounded-full p-1.5 transition-colors', active ? 'bg-primary' : 'bg-white/[0.04] hover:bg-secondary')}>
								<span className="flex h-7 w-7 items-center justify-center rounded-full bg-white">
									<DarePlatformIcon size="16" platform={option} color={PlatformIconColor.Dark}/>
								</span>
							</button>
						</TooltipTrigger>
						<TooltipContent>{(_b = providersLabelMap[option]) !== null && _b !== void 0 ? _b : option}</TooltipContent>
					</Tooltip>);
    })}
		</div>
	</TooltipProvider>);
// One half of the grid/list segmented control. The active state slides between the two buttons via a
// shared-layoutId pill in the toolbar's active tint (border-primary/40 bg-primary/10, matching the
// Filters button when filters are applied).
const ViewToggleButton = ({ active, onClick, label, children }) => (<Tooltip>
		<TooltipTrigger asChild>
			<button type="button" onClick={onClick} aria-label={label} className={cn('relative z-[1] flex h-7 w-7 items-center justify-center rounded-md transition-colors', active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground')}>
				{active && (<motion.span layoutId="dropDaresViewPill" className="absolute inset-0 z-[-1] rounded-md border border-solid border-primary/40 bg-primary/10" transition={{ type: 'spring', stiffness: 400, damping: 28 }}/>)}
				{children}
			</button>
		</TooltipTrigger>
		<TooltipContent>{label}</TooltipContent>
	</Tooltip>);
export const DropDaresSection = memo(({ drop, refetchDropEvent }) => {
    var _a;
    const dares = ((_a = drop.dares) !== null && _a !== void 0 ? _a : []);
    const redesignModal = useRedesignModal();
    const isAuthenticated = useIsAuthenticated();
    const showCreateDare = isAuthenticated && Boolean(drop.enableCreatorDares);
    const onCreateDare = () => redesignModal.show(<RedesignCreateDareModal dropId={drop.id} onCreate={refetchDropEvent}/>);
    const [selectedLevels, setSelectedLevels] = useState([]);
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    // Both display preferences persist across drops. Safe to lazy-init from localStorage: the drop is
    // client-fetched, so this section never appears in server HTML and cannot mismatch on hydration.
    const [storedView, setView] = useLocalStorage('redesign.dropDares.view', 'grid');
    const [showAll, setShowAll] = useLocalStorage('redesign.dropDares.showAll', false);
    // Anything unexpected in storage falls back to the grid.
    const view = storedView === 'list' ? 'list' : 'grid';
    // Page size follows the grid rather than being picked: 3 rows means 9 cards on lg, 6 on sm and 3 on
    // mobile, so the section is the same height at every breakpoint. List view pages by fixed rows.
    const [width] = useWindowSize();
    const pageSize = view === 'list' ? LIST_ROWS_PER_PAGE : columnsForWidth(width) * DARES_ROWS_PER_PAGE;
    const difficulties = useMemo(() => {
        const present = new Set(dares.map(dare => dare.level));
        return DIFFICULTY_ORDER.filter(level => present.has(level));
    }, [dares]);
    const platforms = useMemo(() => Array.from(new Set(dares.flatMap(dare => { var _a; return (_a = dare.platforms) !== null && _a !== void 0 ? _a : []; }))), [dares]);
    const query = search.trim().toLowerCase();
    const filtered = useMemo(() => dares.filter(dare => {
        var _a;
        return ((selectedLevels.length === 0 || selectedLevels.includes(dare.level))
            && (selectedPlatforms.length === 0 || ((_a = dare.platforms) !== null && _a !== void 0 ? _a : []).some(platform => selectedPlatforms.includes(platform)))
            && (query === '' || matchesQuery(dare, query)));
    }), [dares, selectedLevels, selectedPlatforms, query]);
    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    // Clamped rather than read straight from state: every filter change resets the page below, but this
    // also covers the drop refetching into a shorter list while the user sits on a high page.
    const currentPage = Math.min(page, pageCount);
    const pagedDares = useMemo(() => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize), [filtered, currentPage, pageSize]);
    const visibleDares = showAll ? filtered : pagedDares;
    // The show-all toggle only appears when it changes anything (more dares than one page holds).
    const canToggleShowAll = filtered.length > pageSize;
    const showDifficulty = difficulties.length > 1;
    const showPlatform = platforms.length > 1;
    const activeCount = selectedLevels.length + selectedPlatforms.length;
    // Filtering resets to page 1: narrowing the list while on page 3 would otherwise land on an empty page.
    const toggleLevel = (value) => {
        setSelectedLevels(current => toggleValue(current, value));
        setPage(1);
    };
    const togglePlatform = (value) => {
        setSelectedPlatforms(current => toggleValue(current, value));
        setPage(1);
    };
    const clearAll = () => {
        setSelectedLevels([]);
        setSelectedPlatforms([]);
        setPage(1);
    };
    const onSearchChange = (value) => {
        setSearch(value);
        setPage(1);
    };
    // View and show-all changes also restart at page 1: the two views page differently (9 rows vs
    // columns*3 cards), so a kept page index would land on an arbitrary slice.
    const onViewChange = (next) => {
        if (next === view) {
            return;
        }
        setView(next);
        setPage(1);
    };
    const onToggleShowAll = () => {
        setShowAll(!showAll);
        setPage(1);
    };
    if (!dares.length) {
        return null;
    }
    // The animated list itself, shared by the paged and show-all branches. Keys include the view so
    // switching grid/list runs the popLayout exit/enter cross-fade instead of morphing cards into rows.
    const daresList = (<motion.div layout className={view === 'grid' ? 'grid grid-cols-1 auto-rows-fr gap-4 sm:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-2'}>
			<AnimatePresence mode="popLayout">
				{visibleDares.map(dare => (<motion.div key={`${view}-${dare.id}`} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2 }} className={view === 'grid' ? 'h-full min-w-0' : 'min-w-0'}>
						{view === 'grid'
                ? <RedesignDareCard drop={drop} dare={dare} refetchDropEvent={refetchDropEvent}/>
                : <RedesignDareRow drop={drop} dare={dare} refetchDropEvent={refetchDropEvent}/>}
					</motion.div>))}
			</AnimatePresence>
		</motion.div>);
    return (<section id="dares" className="scroll-mt-40 px-4 py-12 sm:px-6 lg:px-8">
			<div className="mx-auto max-w-7xl">
				<div className="flex items-end justify-between gap-4">
					<div>
						<h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">Dares</h2>
						<p className="mt-1 text-sm text-muted-foreground">
							<NumberFlow value={filtered.length} className="font-semibold text-foreground"/>
							{' '}{filtered.length === 1 ? 'dare' : 'dares'} to take on
						</p>
						{/* The badge hides itself for signed-out creators and for anyone the drop gives no dares
        to, so the wrapper drops its spacing when nothing renders inside it. */}
						<div className="mt-2 empty:mt-0">
							<DropDaresLeftBadge drop={drop}/>
						</div>
					</div>
					{/* flex-wrap: with the view toggle added the toolbar no longer fits a phone in one line. */}
					<div className="flex flex-wrap items-center justify-end gap-2">
						<div className="relative w-40 sm:w-56">
							<SearchIcon sx={{ fontSize: 16 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
							<Input value={search} onChange={event => onSearchChange(event.target.value)} placeholder="Search dares..." aria-label="Search dares by title, description or reward" className="pl-9"/>
						</div>
						{showCreateDare && (<Button variant="outline" onClick={onCreateDare} className="rounded-lg text-xs font-semibold">
								<AddIcon sx={{ fontSize: 16 }}/>
								Create a dare
							</Button>)}
						{(showDifficulty || showPlatform) && (<Popover>
								<PopoverTrigger asChild>
									<button type="button" className={cn('flex items-center gap-2 rounded-lg border border-solid px-3 py-2 text-sm font-medium transition-colors', activeCount > 0 ? 'border-primary/40 bg-primary/10 text-foreground' : 'border-white/[0.06] bg-white/[0.04] text-muted-foreground hover:text-foreground')}>
										<FilterListIcon sx={{ fontSize: 16 }}/>
										Filters
										{activeCount > 0 && (<span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-white">{activeCount}</span>)}
									</button>
								</PopoverTrigger>
								<PopoverContent align="end" onOpenAutoFocus={event => event.preventDefault()} className="w-72 space-y-4 p-4">
									{showDifficulty && (<div>
											<p className={groupLabelClass}>Difficulty</p>
											<DifficultyChips options={difficulties} selected={selectedLevels} onToggle={toggleLevel}/>
										</div>)}
									{showPlatform && (<div>
											<p className={groupLabelClass}>Platform</p>
											<PlatformChips options={platforms} selected={selectedPlatforms} onToggle={togglePlatform}/>
										</div>)}
									{activeCount > 0 && (<button type="button" onClick={clearAll} className="flex items-center gap-1.5 rounded-md border border-solid border-white/[0.08] px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground">
											<HighlightOffIcon sx={{ fontSize: 14 }}/>
											Clear all
										</button>)}
								</PopoverContent>
							</Popover>)}
						<TooltipProvider delayDuration={200}>
							<div className="flex shrink-0 items-center gap-1 rounded-lg border border-solid border-white/[0.06] bg-white/[0.04] p-1">
								<ViewToggleButton active={view === 'grid'} onClick={() => onViewChange('grid')} label="Grid view">
									<GridViewIcon sx={{ fontSize: 16 }}/>
								</ViewToggleButton>
								<ViewToggleButton active={view === 'list'} onClick={() => onViewChange('list')} label="List view">
									<ViewListIcon sx={{ fontSize: 16 }}/>
								</ViewToggleButton>
							</div>
						</TooltipProvider>
					</div>
				</div>

				{filtered.length > 0 ? (
        // mt-6 sits on the wrapper, not the grid, so the spacing under the header is the same whether
        // or not PaginatedTable renders its top bar (it only does past one page).
        <div className="mt-6">
						{showAll ? daresList : (<PaginatedTable page={currentPage} pageCount={pageCount} pageSize={pageSize} total={filtered.length} onPageChange={setPage} showPageSize={false} 
            // Clear both the navbar and the sticky section nav (matches the section's own scroll-mt-40).
            scrollAnchorClassName="scroll-mt-40">
								{daresList}
							</PaginatedTable>)}
						{canToggleShowAll && (<div className="mt-4 flex justify-center">
								<button type="button" onClick={onToggleShowAll} className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground">
									{showAll ? 'Show pages' : `Show all (${filtered.length})`}
								</button>
							</div>)}
					</div>) : (<p className="mt-8 text-sm text-muted-foreground">
						{query ? `No dares match "${search.trim()}".` : 'No dares match these filters.'}
					</p>)}
			</div>
		</section>);
});
